import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { websiteConfigSchema, type Prospect, type WebsiteConfig } from '@agency/website-schema';
import { trackProposalEvent } from './analytics.js';

export type ProposalAnalytics = {
  summary: { sessions: number; views: number; averageActiveSeconds: number; deepestScrollPercent: number; tourCompletionRate: number; lastSeenAt: string | null };
  funnel: Array<{ eventType: string; count: number }>;
  devices: Array<{ device: string; count: number }>;
  recentSessions: Array<{ sessionId: string; startedAt: string; lastSeenAt: string; activeSeconds: number; maxScrollPercent: number; device: string; tourCompleted: boolean; claimClicked: boolean; scheduleClicked: boolean }>;
  umamiDashboardUrl: string | null;
};

type ImportedProspectResponse = {
  id: string;
  slug: string;
  createdAt: string;
  businessName: string;
  priceCents: number;
  promotionPriceCents: number | null;
  websiteConfig: WebsiteConfig;
  rendererType: 'static';
  importedSite: NonNullable<Prospect['importedSite']>;
  error?: string;
};

type DataContextValue = {
  prospects: Prospect[];
  loading: boolean;
  getProspect: (slug: string | undefined) => Prospect | undefined;
  refreshProspect: (slug: string) => Promise<void>;
  createProspect: (input: { businessName: string; slug: string; priceCents: number; promotionPriceCents: number | null; websiteConfig: WebsiteConfig; existingLead?: boolean }) => Promise<Prospect>;
  createLead: (input: { businessName: string; slug: string; priceCents: number; promotionPriceCents: number | null; contactName: string | null; contactEmail: string | null; contactPhone: string | null; sourceUrls: string[]; notes: string | null; demoScheduledAt: string | null }) => Promise<Prospect>;
  importProspect: (input: { businessName: string; slug: string; priceCents: number; promotionPriceCents: number | null; archive: File; existingLead?: boolean }) => Promise<Prospect>;
  updateProspectSettings: (slug: string, priceCents: number, promotionPriceCents: number | null, schedulingUrl: string | null) => Promise<void>;
  saveWebsite: (slug: string, config: WebsiteConfig, publish?: boolean) => Promise<void>;
  archiveProspect: (slug: string) => void;
  duplicateProspect: (slug: string) => void;
  track: (slug: string, eventType: string, metadata?: Record<string, unknown>) => void;
  getProspectAnalytics: (slug: string) => Promise<ProposalAnalytics>;
};

const DataContext = createContext<DataContextValue | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const {pathname}=useLocation();
  const [prospects,setProspects]=useState<Prospect[]>([]);
  const [loading,setLoading]=useState(true);
  const persist=(next:Prospect[])=>setProspects(next);
  const refreshProspect=useCallback(async(slug:string)=>{
    const response=await fetch(`/api/prospects/${encodeURIComponent(slug)}`,{headers:{accept:'application/json'}});
    if(!response.ok)throw new Error(`Unable to refresh prospect (${response.status}).`);
    const prospect=await response.json() as Prospect;
    setProspects(current=>current.some(item=>item.slug===slug)?current.map(item=>item.slug===slug?prospect:item):[...current,prospect]);
  },[]);

  useEffect(()=>{
    let active=true;
    const publicMatch=pathname.match(/^\/(?:sites|p|site)\/([a-z0-9]+(?:-[a-z0-9]+)*)/);
    const endpoint=pathname.startsWith('/admin')?'/api/admin/prospects':publicMatch?`/api/prospects/${encodeURIComponent(publicMatch[1]!)}`:null;
    if(!endpoint){setProspects([]);setLoading(false);return;}
    setLoading(true);
    void (async()=>{
      try{
        const response=await fetch(endpoint,{headers:{accept:'application/json'}});
        if(!response.ok)throw new Error(`Unable to load prospects (${response.status}).`);
        const body=await response.json() as Prospect[]|Prospect;
        if(active)setProspects(Array.isArray(body)?body:[body]);
      }catch{
        if(active)setProspects([]);
      }finally{
        if(active)setLoading(false);
      }
    })();
    return()=>{active=false;};
  },[pathname]);

  const value = useMemo<DataContextValue>(() => ({
    prospects,
    loading,
    getProspect: slug => prospects.find(prospect => prospect.slug === slug),
    refreshProspect,
    createProspect: async input => {
      const validated = websiteConfigSchema.parse(input.websiteConfig);
      let slug = input.slug;
      let suffix = 2;
      while (!input.existingLead&&prospects.some(prospect => prospect.slug === slug)) slug = `${input.slug}-${suffix++}`;
      const response=await fetch('/api/admin/prospects',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({businessName:input.businessName,slug,priceCents:input.priceCents,promotionPriceCents:input.promotionPriceCents,config:validated})});
      const body=await response.json() as {id?:string;slug?:string;createdAt?:string;error?:string};
      if(!response.ok||!body.id)throw new Error(body.error||'The prospect could not be saved.');
      const prospect: Prospect = {
        id:body.id,businessName:input.businessName,slug:body.slug??slug,createdAt:body.createdAt??new Date().toISOString(),status:'draft',buildStatus:'in_development',priceCents:input.priceCents,promotionPriceCents:input.promotionPriceCents,schedulingUrl:null,
        views:0,lastViewedAt:null,appointmentAt:null,repositoryUrl:null,contactName:null,contactEmail:validated.business.email??null,contactPhone:validated.business.phone,sourceUrls:[],notes:null,demoScheduledAt:null,websiteConfig:validated,rendererType:'config',
      };
      persist(input.existingLead?prospects.map(item=>item.slug===slug?prospect:item):[...prospects,prospect]);
      return prospect;
    },
    createLead: async input => {
      const response=await fetch('/api/admin/prospects/leads',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(input)});
      const body=await response.json() as Prospect&{error?:string};
      if(!response.ok||!body.id)throw new Error(body.error||'The prospect could not be added to the funnel.');
      const prospect={...body,websiteConfig:websiteConfigSchema.parse(body.websiteConfig)};
      persist([prospect,...prospects]);
      return prospect;
    },
    importProspect: async input => {
      const form = new FormData();
      form.set('businessName', input.businessName);
      form.set('slug', input.slug);
      form.set('priceCents', String(input.priceCents));
      form.set('promotionPriceCents', input.promotionPriceCents == null ? '' : String(input.promotionPriceCents));
      form.set('archive', input.archive, input.archive.name);
      const response = await fetch('/api/admin/prospects/import', { method: 'POST', body: form });
      const body = await response.json() as ImportedProspectResponse;
      if (!response.ok) throw new Error(body.error || 'The website ZIP could not be imported.');
      const prospect: Prospect = {
        id: body.id, businessName: body.businessName, slug: body.slug, createdAt: body.createdAt, status: 'draft', buildStatus:'deployed', priceCents: body.priceCents, promotionPriceCents: body.promotionPriceCents, schedulingUrl:null,
        views: 0, lastViewedAt: null, appointmentAt: null, repositoryUrl: null, contactName:null, contactEmail:null, contactPhone:null, sourceUrls:[], notes:null, demoScheduledAt:null, websiteConfig: websiteConfigSchema.parse(body.websiteConfig),
        rendererType: body.rendererType, importedSite: body.importedSite,
      };
      persist(input.existingLead?prospects.map(item=>item.slug===body.slug?prospect:item):[...prospects, prospect]);
      return prospect;
    },
    updateProspectSettings: async (slug, priceCents, promotionPriceCents, schedulingUrl) => {
      const response=await fetch(`/api/admin/prospects/${encodeURIComponent(slug)}/settings`,{method:'PUT',headers:{'content-type':'application/json'},body:JSON.stringify({priceCents,promotionPriceCents,schedulingUrl})});
      const body=await response.json() as {priceCents?:number;promotionPriceCents?:number|null;schedulingUrl?:string|null;error?:string};
      if(!response.ok||body.priceCents==null)throw new Error(body.error||'The proposal settings could not be saved.');
      persist(prospects.map(prospect=>prospect.slug===slug?{...prospect,priceCents:body.priceCents!,promotionPriceCents:body.promotionPriceCents??null,schedulingUrl:body.schedulingUrl??null}:prospect));
    },
    saveWebsite: async (slug, config, publish = false) => {
      const validated = websiteConfigSchema.parse(config);
      const response=await fetch(`/api/websites/${slug}`,{method:'PUT',headers:{'content-type':'application/json'},body:JSON.stringify({config:validated,publish})});
      const body=await response.json() as {error?:string};
      if(!response.ok)throw new Error(body.error||'The website could not be saved.');
      persist(prospects.map(prospect => prospect.slug === slug ? { ...prospect, websiteConfig: validated } : prospect));
    },
    archiveProspect: slug => persist(prospects.map(prospect => prospect.slug === slug ? { ...prospect, status: 'archived' } : prospect)),
    duplicateProspect: slug => {
      const source = prospects.find(prospect => prospect.slug === slug);
      if (!source) return;
      const suffix = String(Date.now()).slice(-5);
      persist([...prospects, { ...source, id: crypto.randomUUID(), slug: `${source.slug}-copy-${suffix}`, businessName: `${source.businessName} Copy`, status: 'draft', views: 0 }]);
    },
    track: (slug, eventType, metadata = {}) => {
      trackProposalEvent(slug,eventType,metadata);
    },
    getProspectAnalytics: async slug => {
      const response=await fetch(`/api/admin/prospects/${encodeURIComponent(slug)}/analytics`,{headers:{accept:'application/json'}});
      const body=await response.json() as ProposalAnalytics&{error?:string};
      if(!response.ok)throw new Error(body.error||'Proposal analytics could not be loaded.');
      return body;
    },
  }), [loading,prospects,refreshProspect]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useAgencyData() {
  const value = useContext(DataContext);
  if (!value) throw new Error('useAgencyData must be used inside DataProvider');
  return value;
}
